import type {
  OpeningHourT,
  SpecialOpeningHourT,
} from "@/app/types/opening-hour";
import { WEEKDAYS } from "../../constants/weekdays";

// Hilfsfunktion: Gibt für einen Servicebereich und Wochentag die passende Öffnungszeit zurück (Sonderzeit > Standard)
// referenceDate: optional, for client-side use with Europe/Berlin time (avoids server build-time)
export function getOpeningForDay(
  weekday: number,
  opening_hours: OpeningHourT[],
  special_opening_hours: SpecialOpeningHourT[],
  referenceDate?: Date
) {
  // Aktuelles Datum für den Wochentag in dieser Woche bestimmen
  const today = referenceDate ?? new Date();
  const currentWeekDay = today.getDay() === 0 ? 7 : today.getDay(); // JS: 0=So, 1=Mo... → 1=Mo, 7=So
  const diff = weekday - currentWeekDay;
  const date = new Date(today);
  date.setDate(today.getDate() + diff);
  const dateStr = date.toISOString().slice(0, 10); // YYYY-MM-DD

  // Sonderöffnungszeit für dieses Datum?
  const special = special_opening_hours.find((soh) => soh.date === dateStr);
  if (special) {
    return {
      opens: special.opens,
      closes: special.closes,
      is_closed: special.is_closed,
      reason: special.reason,
      special: true,
      periods:
        special.opens && special.closes
          ? [
              {
                opens: special.opens,
                closes: special.closes,
              },
            ]
          : [],
    };
  }

  // Standardöffnungszeiten für diesen Wochentag (können mehrere sein!)
  const dayHours = opening_hours.filter((oh) => oh.weekday === weekday);
  if (dayHours.length === 0) {
    return null;
  }

  // Wenn alle Einträge geschlossen sind, return geschlossen
  if (dayHours.every((h) => h.is_closed)) {
    return {
      opens: null,
      closes: null,
      is_closed: true,
      special: false,
      periods: [],
    };
  }

  // Finde geöffnete Zeiträume und nimm den ersten oder kombiniere sie
  const openHours = dayHours.filter((h) => !h.is_closed);
  if (openHours.length === 0) {
    return {
      opens: null,
      closes: null,
      is_closed: true,
      special: false,
      periods: [],
    };
  }

  // Sortiere nach Öffnungszeit
  const sortedHours = openHours.sort((a, b) => {
    if (!a.opens || !b.opens) return 0;
    return a.opens.localeCompare(b.opens);
  });

  // Gib die einzelnen Zeiträume zurück für korrekte Anzeige
  return {
    opens: sortedHours[0].opens,
    closes: sortedHours[0].closes,
    is_closed: false,
    special: false,
    additional_information: sortedHours[0].additional_information,
    // Neu: Alle Zeiträume für korrekte Anzeige
    periods: sortedHours.map((hour) => ({
      opens: hour.opens,
      closes: hour.closes,
    })),
  };
}

/**
 * Checks if the given time falls within any of the opening periods.
 * Used for real-time "Jetzt geöffnet/geschlossen" status (client-side).
 */
export function isWithinOpeningPeriods(
  periods: Array<{ opens: string; closes: string }>,
  now: Date
): boolean {
  if (!periods || periods.length === 0) return false;
  return periods.some((p) => {
    const [openH, openM] = p.opens.split(":").map(Number);
    const [closeH, closeM] = p.closes.split(":").map(Number);
    const openDate = new Date(now);
    openDate.setHours(openH || 0, openM || 0, 0, 0);
    const closeDate = new Date(now);
    closeDate.setHours(closeH || 0, closeM || 0, 0, 0);
    return now >= openDate && now < closeDate;
  });
}

// Hilfsfunktion: Uhrzeit-String (für einzelne Zeiträume)
export function timeString(opening: ReturnType<typeof getOpeningForDay>) {
  if (!opening || opening.is_closed) return "";
  return `${opening.opens?.slice(0, 5)} – ${opening.closes?.slice(0, 5)} Uhr`;
}

// Hilfsfunktion für Tagesbereich-Label
export function daysLabel(days: number[]) {
  if (days.length === 1) return WEEKDAYS[days[0] - 1].label;
  return `${WEEKDAYS[days[0] - 1].label}–${WEEKDAYS[days[days.length - 1] - 1].label}`;
}
